import type { Channel, LibraryItem } from '@shared/types.ts';
import { usePlayer } from '@/stores/playback.ts';
import { useLibraryHome, useLibrarySections } from '@/lib/queries.ts';
import { ChannelCard } from '@/features/channels/ChannelCard.tsx';
import { SourceBusyDialog } from '@/features/channels/SourceBusyDialog.tsx';
import { useTuneChannel } from '@/features/channels/useTuneChannel.ts';
import { Poster } from './Poster.tsx';
import { useItemMenu } from '@/stores/itemMenu.ts';
import { RequestRow } from './RequestRow.tsx';
import { usePlayItem } from './usePlayItem.ts';
import { Strip } from '@/ui/Strip.tsx';

/**
 * Movies, shows, wrestling and cartoons — whatever Plex is serving.
 *
 * ROWS, NOT A GRID, and the order is not decorative. Continue Watching is
 * first because it is what somebody came here for four times out of five, and
 * every row after it is browsing. A grid of everything is the layout you
 * build when you have not decided what people do with the screen.
 *
 * SIX ROWS, IN THE TELEVISION'S ORDER. The server has sent all six for some
 * time; this screen drew two of them and let the rest fall on the floor,
 * because `libraryHome()` was typed as the two it used to send. So the
 * browser looked thin next to the APK while the data for the missing rows was
 * already arriving in the same response.
 *
 * The order is the one the TV uses, and it is an argument rather than a
 * preference: what you were already watching, then what is on RIGHT NOW
 * (which expires), then what is new and specific (a new episode of a thing
 * you follow), then what is new and general, then what somebody asked for.
 * Urgency descending.
 *
 * The libraries themselves are a strip of words above all of it — see
 * LibraryBar for why they moved up here from the bottom of the page.
 *
 * The poster is the interface. At ten feet nobody reads a title they can
 * recognise from the artwork, so the artwork gets the space and the text is
 * there for the ones you cannot place.
 */
export function LibraryPage() {
  const { data, isLoading, error } = useLibraryHome();
  const { data: sections } = useLibrarySections();
  const { play, open } = usePlayItem();

  const resume = data?.resume ?? [];
  const newMovies = data?.newMovies ?? [];
  const newEpisodes = data?.newEpisodes ?? [];
  const recent = data?.recent ?? [];
  const channels = data?.channels ?? [];

  /* The On Now row IS the lineup for this screen: tune from here and
     left/right cycles through these channels, not through the whole grid. */
  const { tune, busy, listenInstead, dismissBusy } = useTuneChannel(channels);

  if (isLoading) return <div className="muted center">Loading your library…</div>;
  if (error) {
    return (
      <div className="center">
        <div className="panel">
          <h1>Can’t reach Plex</h1>
          <p className="muted">{(error as Error).message}</p>
        </div>
      </div>
    );
  }

  /*
   * WHICH ROW TAKES THE CURSOR ON ARRIVAL. Every row is conditional, so
   * "the first one" cannot be hard-coded to Continue Watching — on a fresh
   * account that row does not exist and nothing would be focused at all.
   */
  const first = resume.length ? 'resume'
    : channels.length ? 'channels'
      : newEpisodes.length ? 'episodes'
        : newMovies.length ? 'movies'
          : 'recent';

  const empty = !resume.length && !recent.length && !newMovies.length
    && !newEpisodes.length && !channels.length;

  return (
    <>
      {/*
        Continue Watching PLAYS rather than opens, even for a series. Resuming
        is the entire purpose of the row, and making somebody walk through a
        series page to reach the episode they were halfway through would be
        the row failing at its one job.
      */}
      {resume.length > 0 && (
        <Row title="Continue watching" items={resume} onSelect={play} first={first === 'resume'} />
      )}

      {channels.length > 0 && (
        <OnNowRow channels={channels} onTune={tune} autoFocus={first === 'channels'} />
      )}

      {newEpisodes.length > 0 && (
        <Row
          /*
           * NOT `wide`, ON PURPOSE.
           *
           * A season-collapsed tile here already renders `images.primary`
           * (see the type comment on LibraryItem.images), which for an
           * episode climbs to the series/season poster — the same field
           * every other row uses. `wide` only ever changed the FRAME around
           * it (.libe-wide: 260px, 16:9), which meant a vertical poster was
           * being force-cropped into a landscape box — bigger than New
           * Movies for no reason, and visibly a poster stretched wrong
           * rather than an actual episode picture. Dropping `wide` here
           * makes this row match New Movies exactly, because it is showing
           * the same kind of artwork.
           *
           * OPEN, NOT PLAY: a tile here may stand for a whole season now —
           * `open` plays a lone episode and opens a season, which is exactly
           * the split this row needs.
           */
          title="New episodes" items={newEpisodes} onSelect={open}
          first={first === 'episodes'}
          /* This row alone wraps into a grid on a phone — see the mobile
             rule for .libe-row-episodes in styles.css for why it, and only
             it, gets that treatment. */
          className="libe-row-episodes"
        />
      )}

      {newMovies.length > 0 && (
        <Row title="New movies" items={newMovies} onSelect={open} first={first === 'movies'} />
      )}

      {recent.length > 0 && (
        <Row title="Recently added" items={recent} onSelect={open} first={first === 'recent'} />
      )}

      {/* After Recently Added, because what the library HAS outranks what it
          has been asked for — but before the empty state, since a house with
          requests in flight is not an empty house. */}
      <RequestRow requests={data?.requests} />

      {/*
        An empty home is not an empty library — it is somebody who has not
        watched anything yet, and the two need different sentences. Telling
        them the library is empty when four hundred films are one press away
        would be a lie that ends the visit.
      */}
      {empty && (
        <div className="muted center">
          {(sections ?? []).length > 0
            ? 'Nothing started yet — pick a library above.'
            : 'Nothing in the library yet.'}
        </div>
      )}

      {busy && (
        <SourceBusyDialog
          active={busy.active}
          onListen={listenInstead}
          onCancel={dismissBusy}
        />
      )}
    </>
  );
}

/**
 * WHAT IS ON RIGHT NOW, which is the one row with a clock on it.
 *
 * Reuses ChannelCard rather than drawing a poster with a logo in it, and that
 * is the whole reason this row is cheap: the card subscribes to its own
 * now-playing metadata while mounted and drops the subscription when it
 * scrolls away, so the server polls exactly the channels somebody is looking
 * at. A bespoke tile here would need all of that again, and would be a second
 * place for "which channels are we watching" to be decided.
 */
function OnNowRow({
  channels, onTune, autoFocus,
}: {
  channels: Channel[];
  onTune(channel: Channel): void;
  autoFocus?: boolean;
}) {
  const playing = usePlayer().channel;

  return (
    <section className="libe-row">
      <h2 className="libe-title">On now</h2>
      <Strip className="onnow-strip">
        {channels.map((channel, i) => (
          <ChannelCard
            key={channel.id}
            channel={channel}
            onPlay={onTune}
            isPlaying={playing?.id === channel.id}
            favourite={channel.favourite}
            autoFocus={autoFocus && i === 0}
          />
        ))}
      </Strip>
    </section>
  );
}

function Row({
  title, items, onSelect, first, wide, className,
}: {
  title: string;
  items: LibraryItem[];
  onSelect(item: LibraryItem): void;
  first?: boolean;
  wide?: boolean;
  className?: string;
}) {
  return (
    <section className={className ? `libe-row ${className}` : 'libe-row'}>
      <h2 className="libe-title">{title}</h2>
      {/* Padded for the focus ring AND the focused card's growth — see
          .libe-strip. A scroll container that clips the cursor is a cursor
          that disappears on a television. */}
      <Strip>
        {items.map((item, i) => (
          <Poster
            key={item.id}
            item={item}
            onSelect={onSelect}
            wide={wide}
            /* Continue Watching is the row this was asked for: a film somebody
               bounced off sits at the front of it forever otherwise. */
            onOptions={(it) => useItemMenu.getState().open(it.id)}
            autoFocus={first && i === 0}
          />
        ))}
      </Strip>
    </section>
  );
}
